import {AppError} from '../util/errors'
import {
  DEFAULT_ANIMATION,
  DEFAULT_AUDIO,
  DEFAULT_GREETING_DELAY_SECONDS,
  DEFAULT_INTRO_CROSSFADE_SECONDS,
  DEFAULT_MODEL,
  DEFAULT_PLACEMENT,
  DEFAULT_SCENE,
  DEFAULT_SOUND_VOLUME,
} from './manifest.defaults'
import type {
  AccentColorSource,
  AdditiveClipConfig,
  AnimationConfig,
  AudioConfig,
  CampaignManifest,
  EnvironmentMode,
  GreetingConfig,
  IntroConfig,
  LoopMode,
  ModelConfig,
  PlacementConfig,
  PlacementEffect,
  PlacementLeadIn,
  ReactionConfig,
  SceneConfig,
  SoundConfig,
} from './manifest.types'

/**
 * Turns the untyped contents of `public/campaign.json` into a fully-defaulted
 * `CampaignManifest`, or fails with a Japanese message naming the exact key.
 *
 * Hand-written guards rather than a schema library: the surface is small, and
 * pulling in a validator to check nine fields is not a trade worth making. The
 * cost is discipline - every key must be read through one of the helpers below
 * so the error messages stay uniform.
 */

const LOOP_MODES: readonly LoopMode[] = ['repeat', 'once', 'pingpong']
const ENVIRONMENT_MODES: readonly EnvironmentMode[] = ['room', 'none']
const PLACEMENT_EFFECTS: readonly PlacementEffect[] = ['landing', 'none']
const PLACEMENT_LEAD_INS: readonly PlacementLeadIn[] = ['footprints', 'none']
const ACCENT_COLOR_SOURCES: readonly AccentColorSource[] = ['model', 'manifest']

/**
 * `#RGB` and `#RRGGBB` only.
 *
 * Deliberately narrower than `THREE.Color.setStyle`, which also accepts CSS
 * colour names, `rgb()` and `hsl()`. Those buy nothing for a single accent
 * colour, and - decisively - `setStyle` only console-warns on input it cannot
 * parse and leaves the colour UNCHANGED, so a typo would ship as a silently
 * wrong colour instead of a parse-time error naming the key. `#RRGGBBAA` is
 * rejected here on purpose: `setStyle` warns on it too.
 */
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const invalid = (path: string, expected: string, actual: unknown): AppError =>
  new AppError('MANIFEST_INVALID', `${path}: expected ${expected}, got ${JSON.stringify(actual)}`, {
    userMessageJa: `設定ファイル(campaign.json)の「${path}」が正しくありません。${expected}を指定してね。`,
  })

/** Reads an optional string; `undefined` falls through to the default. */
const optString = (source: Record<string, unknown>, key: string, path: string): string | undefined => {
  const value = source[key]
  if (value === undefined) return undefined
  if (typeof value !== 'string') throw invalid(path, '文字列', value)
  return value
}

/** Reads an optional finite number, with an optional sign requirement. */
const optNumber = (
  source: Record<string, unknown>,
  key: string,
  path: string,
  opts: {readonly positive?: boolean; readonly nonNegative?: boolean; readonly max?: number} = {}
): number | undefined => {
  const value = source[key]
  if (value === undefined) return undefined
  if (typeof value !== 'number' || !Number.isFinite(value)) throw invalid(path, '数値', value)
  if (opts.positive === true && value <= 0) throw invalid(path, '0より大きい数値', value)
  // Distinct from `positive` because a duration of 0 is meaningful - it means
  // "cut straight over", which is the right answer for a well-authored intro.
  if (opts.nonNegative === true && value < 0) throw invalid(path, '0以上の数値', value)
  // Rejected, never clamped: a `5` written where `0.5` was meant is a typo, and
  // silently playing at full volume hides it until someone is holding a phone.
  if (opts.max !== undefined && value > opts.max) {
    throw invalid(path, `${opts.max}以下の数値`, value)
  }
  return value
}

const optBoolean = (source: Record<string, unknown>, key: string, path: string): boolean | undefined => {
  const value = source[key]
  if (value === undefined) return undefined
  if (typeof value !== 'boolean') throw invalid(path, 'true または false', value)
  return value
}

/** Reads a `T | null` field where `null` is meaningful, not merely absent. */
const optNullableNumber = (
  source: Record<string, unknown>,
  key: string,
  path: string
): number | null | undefined => {
  if (source[key] === null) return null
  return optNumber(source, key, path, {positive: true})
}

const optNullableString = (
  source: Record<string, unknown>,
  key: string,
  path: string
): string | null | undefined => {
  if (source[key] === null) return null
  return optString(source, key, path)
}

const optEnum = <T extends string>(
  source: Record<string, unknown>,
  key: string,
  path: string,
  allowed: readonly T[]
): T | undefined => {
  const value = source[key]
  if (value === undefined) return undefined
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw invalid(path, allowed.map((v) => `"${v}"`).join(' / '), value)
  }
  return value as T
}

/** Reads an optional `#RGB` / `#RRGGBB` colour. See `HEX_COLOR` for why it is this narrow. */
const optHexColor = (
  source: Record<string, unknown>,
  key: string,
  path: string
): string | undefined => {
  const value = optString(source, key, path)
  if (value === undefined) return undefined
  if (!HEX_COLOR.test(value)) throw invalid(path, '#RRGGBB 形式の色', value)
  return value
}

const optRecord = (
  source: Record<string, unknown>,
  key: string,
  path: string
): Record<string, unknown> => {
  const value = source[key]
  if (value === undefined) return {}
  if (!isRecord(value)) throw invalid(path, 'オブジェクト', value)
  return value
}

/**
 * The `clip` key that every optional clip block requires.
 *
 * Extracted so `greeting` could get its own parser without also getting its own
 * copy of this message. The header's rule is that every key is read through one
 * of these helpers precisely so a new block cannot arrive with slightly
 * different Japanese; `noun` is the one part that is allowed to vary, because
 * "登場アニメのクリップ名" is genuinely more useful than "クリップ名" at the one
 * site that can say it.
 *
 * There is no first-clip fallback anywhere here, unlike `model.animation.clip`.
 * Guessing which clip is the entrance animation would silently play the wrong
 * one, and an overlay pointed at an arbitrary clip layers that clip on top of
 * everything else - both look like a rig bug rather than the config typo they
 * are.
 */
const requireClipName = (
  value: Record<string, unknown>,
  path: string,
  noun = 'クリップ名'
): string => {
  const clip = optString(value, 'clip', `${path}.clip`)
  if (clip === undefined || clip.length === 0) {
    throw new AppError('MANIFEST_INVALID', `${path}.clip is required`, {
      userMessageJa: `設定ファイル(campaign.json)の「${path}.clip」がありません。${noun}を指定してね。`,
    })
  }
  return clip
}

/**
 * Reads `model.animation.intro`. Both an absent key and an explicit `null` mean
 * "no entrance animation", so a campaign can turn one off without deleting the
 * block and losing the clip name it had.
 */
const parseIntro = (raw: Record<string, unknown>): IntroConfig | null => {
  const value = raw['intro']
  if (value === undefined || value === null) return DEFAULT_ANIMATION.intro
  if (!isRecord(value)) throw invalid('model.animation.intro', 'オブジェクト', value)

  return {
    clip: requireClipName(value, 'model.animation.intro', '登場アニメのクリップ名'),
    crossFadeSeconds:
      optNumber(value, 'crossFadeSeconds', 'model.animation.intro.crossFadeSeconds', {
        nonNegative: true,
      }) ?? DEFAULT_INTRO_CROSSFADE_SECONDS,
  }
}

/**
 * The `clips` list that `reaction` requires - `requireClipName` for a pool.
 *
 * Every name is checked here rather than downstream so a typo is named by its
 * INDEX (`...clips[1]`) while the index still means something; the animation
 * controller only ever sees names, and by then "one of them is wrong" is the
 * best message anybody could write.
 *
 * An empty array is rejected rather than treated as `null`. `null` already
 * means "no reaction" and says so at the block level; `[]` reads as an
 * unfinished edit, and silently accepting it would turn the model's whole
 * silhouette back into placement surface with nothing to point at.
 */
const requireClipNames = (value: Record<string, unknown>, path: string): readonly string[] => {
  const clips = value['clips']
  if (!Array.isArray(clips) || clips.length === 0) {
    throw invalid(`${path}.clips`, '1つ以上のクリップ名が入った配列', clips)
  }
  return clips.map((name, index) => {
    if (typeof name !== 'string' || name.length === 0) {
      throw invalid(`${path}.clips[${index}]`, '文字列', name)
    }
    return name
  })
}

/**
 * Reads `model.animation.blink`. An absent key and an explicit `null` both mean
 * off, so a campaign can disable it without deleting the block and losing the
 * clip name it had - the same bargain `model.animation.intro` and every `audio`
 * slot make.
 *
 * This used to be one function shared with `reaction`, on the promise that it
 * would split "when they diverge rather than growing a flag". `reaction` became
 * a pool of clips; this is that split, and `greeting` took the same exit one
 * parser earlier.
 */
const parseBlink = (raw: Record<string, unknown>): AdditiveClipConfig | null => {
  const value = raw['blink']
  if (value === undefined || value === null) return DEFAULT_ANIMATION.blink
  if (!isRecord(value)) throw invalid('model.animation.blink', 'オブジェクト', value)

  return {clip: requireClipName(value, 'model.animation.blink')}
}

/**
 * Reads `model.animation.reaction` - the pool the tap draws from. Absent and
 * explicit `null` both mean off, like every other optional block.
 */
const parseReaction = (raw: Record<string, unknown>): ReactionConfig | null => {
  const value = raw['reaction']
  if (value === undefined || value === null) return DEFAULT_ANIMATION.reaction
  if (!isRecord(value)) throw invalid('model.animation.reaction', 'オブジェクト', value)

  return {clips: requireClipNames(value, 'model.animation.reaction')}
}

/**
 * Reads `model.animation.greeting` - the additive clip scheduled a beat after
 * the entrance animation ends. Absent and explicit `null` both mean off, like
 * every other optional block.
 */
const parseGreeting = (raw: Record<string, unknown>): GreetingConfig | null => {
  const value = raw['greeting']
  if (value === undefined || value === null) return DEFAULT_ANIMATION.greeting
  if (!isRecord(value)) throw invalid('model.animation.greeting', 'オブジェクト', value)

  return {
    clip: requireClipName(value, 'model.animation.greeting', 'あいさつのクリップ名'),
    // `nonNegative`, not `positive`: 0 means "greet the instant the entrance
    // ends", which is a legitimate authoring choice - the same distinction
    // `intro.crossFadeSeconds` makes.
    delaySeconds:
      optNumber(value, 'delaySeconds', 'model.animation.greeting.delaySeconds', {
        nonNegative: true,
      }) ?? DEFAULT_GREETING_DELAY_SECONDS,
  }
}

const parseAnimation = (raw: Record<string, unknown>): AnimationConfig => {
  // `null` is a meaningful value for `clip` (use the first clip), so it must be
  // distinguished from `undefined` (key absent) rather than folded in with `??`.
  const clip = optNullableString(raw, 'clip', 'model.animation.clip')

  return {
    clip: clip === undefined ? DEFAULT_ANIMATION.clip : clip,
    loop: optEnum(raw, 'loop', 'model.animation.loop', LOOP_MODES) ?? DEFAULT_ANIMATION.loop,
    intro: parseIntro(raw),
    blink: parseBlink(raw),
    reaction: parseReaction(raw),
    greeting: parseGreeting(raw),
    timeScale:
      optNumber(raw, 'timeScale', 'model.animation.timeScale', {positive: true}) ??
      DEFAULT_ANIMATION.timeScale,
  }
}

const parseModel = (raw: Record<string, unknown>): ModelConfig => {
  const url = optString(raw, 'url', 'model.url')
  if (url === undefined || url.length === 0) {
    throw new AppError('MANIFEST_INVALID', 'model.url is required', {
      userMessageJa: '設定ファイル(campaign.json)に「model.url」がありません。GLBのパスを指定してね。',
    })
  }

  // `?? DEFAULT` is safe for targetHeightMeters because `null` is a meaningful
  // value here (keep authored scale) and `?? ` only falls through on undefined.
  const targetHeightMeters = optNullableNumber(raw, 'targetHeightMeters', 'model.targetHeightMeters')

  return {
    url,
    targetHeightMeters:
      targetHeightMeters === undefined ? DEFAULT_MODEL.targetHeightMeters : targetHeightMeters,
    yawDegrees: optNumber(raw, 'yawDegrees', 'model.yawDegrees') ?? DEFAULT_MODEL.yawDegrees,
    yOffsetMeters: optNumber(raw, 'yOffsetMeters', 'model.yOffsetMeters') ?? DEFAULT_MODEL.yOffsetMeters,
    castShadow: optBoolean(raw, 'castShadow', 'model.castShadow') ?? DEFAULT_MODEL.castShadow,
    animation: parseAnimation(optRecord(raw, 'animation', 'model.animation')),
  }
}

const parseScene = (raw: Record<string, unknown>): SceneConfig => ({
  environment:
    optEnum(raw, 'environment', 'scene.environment', ENVIRONMENT_MODES) ?? DEFAULT_SCENE.environment,
})

/**
 * One `audio` slot. An absent key and an explicit `null` both mean "silent
 * here", so a campaign can mute one moment without deleting the block and losing
 * the URL it had - exactly like `model.animation.intro`.
 *
 * The object body lives in `parseSound`, shared with the react map below, on
 * the same terms as `requireClipName` above: one function, and the path is the
 * only thing allowed to vary. Copies of it would be as many chances for the
 * Japanese to drift apart, which is the failure the header's rule exists to
 * prevent.
 */
const parseSoundSlot = (
  raw: Record<string, unknown>,
  key: 'step' | 'arrive' | 'shutter'
): SoundConfig | null => {
  const value = raw[key]
  if (value === undefined || value === null) return DEFAULT_AUDIO[key]
  return parseSound(value, `audio.${key}`)
}

/** One `{url, volume?}` object at `path`, shared by the slots and the react map. */
const parseSound = (value: unknown, path: string): SoundConfig => {
  if (!isRecord(value)) throw invalid(path, 'オブジェクト', value)

  const url = optString(value, 'url', `${path}.url`)
  if (url === undefined || url.length === 0) {
    throw new AppError('MANIFEST_INVALID', `${path}.url is required`, {
      userMessageJa: `設定ファイル(campaign.json)の「${path}.url」がありません。音声ファイルのパスを指定してね。`,
    })
  }

  return {
    url,
    volume:
      optNumber(value, 'volume', `${path}.volume`, {nonNegative: true, max: 1}) ??
      DEFAULT_SOUND_VOLUME,
  }
}

/**
 * The tap-reaction map. Keys are clip names, so unlike the fixed slots they are
 * not validated here - the parser has never seen the GLB. `check-assets.mjs`
 * owns that check, where both the map and the reaction pool are in hand.
 *
 * An entry set to `null` mutes that one clip without deleting its URL from the
 * file - the same bargain the three fixed slots offer.
 */
const parseReactSounds = (raw: Record<string, unknown>): AudioConfig['react'] => {
  const value = raw['react']
  if (value === undefined || value === null) return DEFAULT_AUDIO.react
  if (!isRecord(value)) throw invalid('audio.react', 'オブジェクト', value)
  return Object.fromEntries(
    Object.entries(value).flatMap(([clip, sound]) =>
      sound === null ? [] : [[clip, parseSound(sound, `audio.react.${clip}`)] as const]
    )
  )
}

const parseAudio = (raw: Record<string, unknown>): AudioConfig => ({
  step: parseSoundSlot(raw, 'step'),
  arrive: parseSoundSlot(raw, 'arrive'),
  shutter: parseSoundSlot(raw, 'shutter'),
  react: parseReactSounds(raw),
})

const parsePlacement = (raw: Record<string, unknown>): PlacementConfig => ({
  effect: optEnum(raw, 'effect', 'placement.effect', PLACEMENT_EFFECTS) ?? DEFAULT_PLACEMENT.effect,
  leadIn:
    optEnum(raw, 'leadIn', 'placement.leadIn', PLACEMENT_LEAD_INS) ?? DEFAULT_PLACEMENT.leadIn,
  accentColorSource:
    optEnum(raw, 'accentColorSource', 'placement.accentColorSource', ACCENT_COLOR_SOURCES) ??
    DEFAULT_PLACEMENT.accentColorSource,
  // Validated even when `accentColorSource` is 'model': it is the fallback, and
  // a typo in a value that only surfaces on the models where extraction fails
  // is exactly the kind of bug that ships.
  accentColor:
    optHexColor(raw, 'accentColor', 'placement.accentColor') ?? DEFAULT_PLACEMENT.accentColor,
})

/** Validates already-parsed JSON. Exported so it can be unit-tested without a fetch. */
export const parseManifest = (value: unknown): CampaignManifest => {
  if (!isRecord(value)) {
    throw new AppError('MANIFEST_INVALID', 'root is not an object', {
      userMessageJa: '設定ファイル(campaign.json)の形式が正しくありません。',
    })
  }

  const model = value['model']
  if (!isRecord(model)) throw invalid('model', 'オブジェクト', model)

  return {
    model: parseModel(model),
    scene: parseScene(optRecord(value, 'scene', 'scene')),
    placement: parsePlacement(optRecord(value, 'placement', 'placement')),
    audio: parseAudio(optRecord(value, 'audio', 'audio')),
  }
}

export const loadManifest = async (url: string, signal?: AbortSignal): Promise<CampaignManifest> => {
  let response: Response
  try {
    response = await fetch(url, signal ? {signal} : undefined)
  } catch (cause) {
    throw new AppError('MANIFEST_INVALID', `fetch ${url} failed`, {
      cause,
      userMessageJa: '設定ファイルを読み込めませんでした。通信環境を確認してね。',
    })
  }

  if (!response.ok) {
    throw new AppError('MANIFEST_INVALID', `${url} returned ${response.status}`, {
      userMessageJa: `設定ファイル(campaign.json)が見つかりません (${response.status})。`,
    })
  }

  let json: unknown
  try {
    json = await response.json()
  } catch (cause) {
    throw new AppError('MANIFEST_INVALID', `${url} is not valid JSON`, {
      cause,
      userMessageJa: '設定ファイル(campaign.json)のJSONが壊れています。カンマや括弧を確認してね。',
    })
  }

  return parseManifest(json)
}
